import { Button } from '@iwoca/orion';
import { FormProvider, useForm } from 'react-hook-form';

import { useUpdateTurnover } from '../../../hooks/useUpdateTurnover';

type FormValues = {
  turnover: string;
};

export const UpdateTurnover = ({
  refreshSpendingLimit,
  loadingRefreshSpendingLimit,
}: {
  refreshSpendingLimit: () => Promise<void>;
  loadingRefreshSpendingLimit: boolean;
}) => {
  const { updateTurnover, loadingTurnover: loadingUpdateTurnover } =
    useUpdateTurnover();

  const methods = useForm<FormValues>({
    defaultValues: {
      turnover: '',
    },
  });

  const handleSubmit = async ({ turnover }: FormValues) => {
    const sanitizedTurnover = turnover.replace(/,/g, '');
    await updateTurnover(Number(sanitizedTurnover));
    await refreshSpendingLimit();
  };

  const renderButtonText = () => {
    if (loadingUpdateTurnover) {
      return (
        <>
          <svg
            className="h-4 w-4 animate-spin"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          Updating turnover...
        </>
      );
    }
    if (loadingRefreshSpendingLimit) {
      return (
        <>
          <svg
            className="h-4 w-4 animate-spin"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          Checking limits...
        </>
      );
    }
    return 'See what you can spend';
  };

  const onSubmit = methods.handleSubmit(handleSubmit);

  const sanitiseTurnover = (value: string) => {
    const sanitizedValue = value.replace(/[^0-9]/g, '');
    return sanitizedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  return (
    <FormProvider {...methods}>
      <form className="border-t-gray-300 border-t" onSubmit={onSubmit}>
        <div className="gap-2 flex flex-col">
          <div className="pt-6 text-2xl font-bold">
            First, we need to check some numbers
          </div>
          <div className="font-reg">
            It's been a while since we asked you for an update.
          </div>
        </div>
        <div className="pt-6">
          <div className="text-base font-medium">
            Turnover for the last 12 months
          </div>
          <div className="text-s font-reg">
            This doesn't have to be exact to the penny
          </div>
        </div>
        <div className="pt-3 relative flex items-center">
          <span className="left-3 text-base absolute pb-[1px]">&pound;</span>
          <input
            name="turnover"
            placeholder="Enter amount..."
            type="text"
            className="rounded border-gray-300 p-3 pl-8 w-full border"
            onChange={(e) => {
              methods.setValue('turnover', sanitiseTurnover(e.target.value));
            }}
            value={methods.watch('turnover')}
          />
        </div>
        <div className="pt-6">
          <Button
            className="w-full"
            variant="primary"
            disabled={loadingUpdateTurnover || loadingRefreshSpendingLimit}
          >
            {renderButtonText()}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
